import { Component, ChangeDetectionStrategy, ChangeDetectorRef,
  EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { BeatTypeConsolAddService } from './beat-type-consol-add.service';
import {
  AppkitDropdownItem,
  findAppkitItemByValue,
  getAppkitDropdownSelection,
  mapToAppkitDropdownItems,
  syncAppkitDropdownSelection
} from '../../../shared/utils/appkit-dropdown.util';
import { setFormDropdownAriaLabel } from '../../shared/ftc-form-dropdown.support';

@Component({
    selector: 'gtw-beat-type-consol-add',
    templateUrl: './beat-type-consol-add.component.html',
    styleUrls: ['./beat-type-consol-add.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BeatTypeConsolAddComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  currentTabName = 'Form Entry';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = false;
  activeTab = 1;

  entityList: any[] = [];
  leId = '';
  cdr_no = '';
  leType = '';

  selectedBeatRow: any[] = [];
  beatTypeList: any[] = [];
  copyBeatTypeList: any[] = [];
  beatTypeData: any[] = [];
  countryData: any[] = [];
  foreignTradingPartnerCodeData: any[] = [];

  beatTypeOptions: any[] = [];
  tradingPartnerOptions: any[] = [];
  countryOptions: any[] = [];

  beatTypeOptionsView: AppkitDropdownItem[] = [];
  tradingPartnerOptionsView: AppkitDropdownItem[] = [];
  countryOptionsView: AppkitDropdownItem[] = [];

  setFormDropdownAriaLabel = setFormDropdownAriaLabel;

  total = 0;
  totalBeatPayment = 0;

  isEditMode = false;
  isAssetEditMode = false;
  pshipFlag = 'N';
  isRAF = true;
  is_locked: any[] = [];

  modalTitle = 'Add BEAT by Consol';

  filterParams: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  rowData: any = {};

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private beatTypeConsolAddService: BeatTypeConsolAddService,
    private _snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  trackByIndex(index: number): number {
    return index;
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0
        && this.filterParams && Object.keys(this.filterParams).length > 0
        && this.baseURLs && Object.keys(this.baseURLs).length > 0) {
        this.isInitialized = true;
        this.setupEntityData();
        this.loadComponentData();
      }
    } catch (e) {
      console.error('Error in BeatTypeConsolAdd init:', e);
    }
  }

  private setupEntityData(): void {
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;

    this.selectedBeatRow = [this.rowData];
    this.pshipFlag = this.rowData.IS_PSHIP_TIER || 'N';
    this.isRAF = this.rowData.SCENARIO_TYPE_CODE === undefined || this.rowData.SCENARIO_TYPE_CODE === '';

    this.isAssetEditMode = !!this.rowData.ADJ_KEY;
    this.modalTitle = this.isAssetEditMode ? 'Edit BEAT by Consol' : 'Add BEAT by Consol';

    this.entityList = [
      { key: 'Tax Year', value: this.rowData.TAX_YEAR },
      { key: 'LEID', value: this.rowData.LEID },
      { key: 'LE Name', value: this.rowData.LE_NAME },
    ];
  }

  private loadComponentData(): void {
    this.showFormLoader = true;
    this.cdr.markForCheck();

    this.beatTypeConsolAddService.loadTradingPartnerData(
      this.baseURLs.api,
      this.filterParams,
      this.rowData,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.errorMessage && data.errorMessage !== 'null') {
          this._snackBar.open(data.errorMessage, 'Close', {
            duration: 4000, panelClass: ['error-snackbar']
          });
        } else {
          this.processTradingPartnerData(data.jsonObject);
          this.getPopUpData();
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.showFormLoader = false;
        this._snackBar.open('Error loading trading partner data', 'Close', {
          duration: 4000, panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  private processTradingPartnerData(data: any[]): void {
    this.beatTypeData = data.filter(p => p.CODE_GROUP === 'BEAT_TYPE_CODE');
    this.countryData = data.filter(p => p.CODE_GROUP === 'COUNTRY_CODE');
    this.foreignTradingPartnerCodeData = data.filter(p => p.CODE_GROUP === 'RELATED_FGN_BEAT_YES');

    this.beatTypeOptions = this.beatTypeData.map(item => ({
      label: item.CODE_DESCRIPTION,
      value: item.CODE_KEY,
    }));
    this.tradingPartnerOptions = this.foreignTradingPartnerCodeData.map(item => ({
      label: item.LE_NAME,
      value: item.LE_NAME,
    }));
    this.countryOptions = this.countryData.map(item => ({
      label: item.CODE_DESCRIPTION,
      value: item.CODE_KEY,
    }));
    this.refreshDropdownViews();
  }

  private refreshDropdownViews(): void {
    this.beatTypeOptionsView = mapToAppkitDropdownItems(this.beatTypeOptions, 'label', 'value');
    this.tradingPartnerOptionsView = mapToAppkitDropdownItems(this.tradingPartnerOptions, 'label', 'value');
    this.countryOptionsView = mapToAppkitDropdownItems(this.countryOptions, 'label', 'value');
    this.syncAllRowDropdownItems();
  }

  private syncRowDropdownItems(row: any): void {
    row.selectedBeatTypeItem = syncAppkitDropdownSelection(
      this.beatTypeOptionsView,
      row.selectedBeatTypeCode,
      'label',
      'value'
    );
    row.selectedTradingPartnerItem = syncAppkitDropdownSelection(
      this.tradingPartnerOptionsView,
      row.selectedTradingPartner,
      'label',
      'value'
    );
    row.selectedCountryItem = syncAppkitDropdownSelection(
      this.countryOptionsView,
      row.selectedCountryCode,
      'label',
      'value'
    );
  }

  private syncAllRowDropdownItems(): void {
    this.beatTypeList.forEach((row) => this.syncRowDropdownItems(row));
  }

  private getPopUpData(): void {
    this.beatTypeConsolAddService.loadPopUpData(
      this.baseURLs.api,
      this.filterParams,
      this.rowData,
      this.selectedBeatRow,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.showFormLoader = false;
        if (data.errorMessage && data.errorMessage !== 'null') {
          this._snackBar.open(data.errorMessage, 'Close', {
            duration: 4000, panelClass: ['error-snackbar']
          });
        } else {
          this.processPopUpData(data.jsonObject);
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.showFormLoader = false;
        this._snackBar.open('Error loading popup data', 'Close', {
          duration: 4000, panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  private processPopUpData(data: any[]): void {
    this.total = 0;
    this.totalBeatPayment = 0;

    if (data && data.length > 0) {
      this.beatTypeList = data.map(item => {
        this.total += parseInt(item.ADJ_AMT || 0);
        this.totalBeatPayment += parseInt(item.BEAT_PAYMENT || 0);
        return {
          amount: item.ADJ_AMT,
          beat_payment: item.BEAT_PAYMENT,
          beatWhPercent: item.ADJ_PERCENT,
          oldAmount: item.ADJ_AMT,
          oldBeatWhPercent: item.ADJ_PERCENT,
          selectedBeatTypeCode: item.ADJ_TYPE_KEY,
          selectedTradingPartnerCode: item.COMBINATION_KEY,
          selectedTradingPartner: item.LEID,
          adjKey: item.ADJ_KEY,
          OPERATION_TYPE: 'U',
          selectedCountryCode: item.LE_COUNTRY,
          groupKey: item.GROUP_KEY,
          comments: item.COMMENTS,
          issueID: item.ISSUE_KEY,
        };
      });
      this.isEditMode = true;
      this.copyBeatTypeList = _.cloneDeep(this.beatTypeList);
    } else {
      this.beatTypeList = [this.createEmptyRow()];
    }
    this.syncAllRowDropdownItems();
  }

  private createEmptyRow(): any {
    const row = {
      selectedBeatTypeCode: null,
      selectedTradingPartnerCode: null,
      selectedTradingPartner: null,
      selectedCountryCode: null,
      selectedBeatTypeItem: null,
      selectedTradingPartnerItem: null,
      selectedCountryItem: null,
      amount: '',
      beat_payment: '',
      beatWhPercent: '',
      oldAmount: 0,
      oldBeatWhPercent: 0,
      comments: '',
      OPERATION_TYPE: 'I',
      issueID: this.rowData.ISSUE_KEY || '',
    };
    this.syncRowDropdownItems(row);
    return row;
  }

  addRow(): void {
    this.beatTypeList = [...this.beatTypeList, this.createEmptyRow()];
    this.cdr.markForCheck();
  }

  deleteRow(selectedRowData: any): void {
    const index = this.beatTypeList.indexOf(selectedRowData);
    if (index < 0) return;

    let updated: any[];
    if (selectedRowData.OPERATION_TYPE === 'I') {
      updated = this.beatTypeList.filter((_, i) => i !== index);
    } else {
      updated = this.beatTypeList.map((row, i) =>
        i === index ? { ...row, OPERATION_TYPE: 'D' } : row
      );
    }

    const countActiveRows = updated.filter(r => r.OPERATION_TYPE !== 'D').length;
    if (countActiveRows === 0) {
      updated = [...updated, this.createEmptyRow()];
    }

    this.beatTypeList = updated;
    this.recalcTotals();
    this.cdr.markForCheck();
  }

  changeTradingPartner(data: any): void {
    const exists = this.foreignTradingPartnerCodeData.find(item => item.LE_NAME === data.selectedTradingPartner);
    if (exists) {
      const countryExists = this.countryData.find(item => item.CODE_SHORT_DESC === exists.LE_COUNTRY);
      data.selectedCountryCode = countryExists ? countryExists.CODE_KEY : null;
    } else {
      data.selectedCountryCode = null;
    }
    data.selectedCountryItem = findAppkitItemByValue(this.countryOptionsView, data.selectedCountryCode);
    this.cdr.markForCheck();
  }

  onBeatTypeRowSelect(event: any, row: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      row.selectedBeatTypeItem = selected;
      row.selectedBeatTypeCode = selected.value;
    }
    this.cdr.markForCheck();
  }

  onTradingPartnerRowSelect(event: any, row: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      row.selectedTradingPartnerItem = selected;
      row.selectedTradingPartner = selected.value;
      this.changeTradingPartner(row);
    }
  }

  onCountryRowSelect(event: any, row: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      row.selectedCountryItem = selected;
      row.selectedCountryCode = selected.value;
    }
    this.cdr.markForCheck();
  }

  onDropdownSelectItem(_event: any): void {}

  updateTotal(): void {
    this.recalcTotals();
    this.cdr.markForCheck();
  }

  updateBeatPaymentTotal(): void {
    this.recalcTotals();
    this.cdr.markForCheck();
  }

  private recalcTotals(): void {
    this.total = 0;
    this.totalBeatPayment = 0;
    for (const item of this.beatTypeList) {
      if (item.OPERATION_TYPE && item.OPERATION_TYPE !== 'D') {
        this.total += parseInt(item.amount || 0);
        this.totalBeatPayment += parseInt(item.beat_payment || 0);
      }
    }
  }

  changeInPercent(index: number, beatWhPercent: number): void {
    const hasInvalid = this.beatTypeList.some(
      info => info && (info.beatWhPercent < 0 || info.beatWhPercent > 100)
    );

    if (hasInvalid) {
      this._snackBar.open('Please select BEAT Withhold between 0 to 100 only', 'Close', {
        duration: 5000, panelClass: ['warning-snackbar']
      });
      this.isSaveClicked = true;
    } else {
      this.isSaveClicked = false;
    }
    this.cdr.markForCheck();
  }

  save(): void {
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.markForCheck();

    let totalSubAmount = 0;
    for (const row of this.beatTypeList) {
      totalSubAmount += row.amount || 0;

      if ((!row.selectedBeatTypeCode || !row.beatWhPercent || !row.selectedTradingPartner ||
           !row.selectedCountryCode || !row.amount) && row.OPERATION_TYPE !== 'D') {
        this._snackBar.open('Please select/enter all the required fields.', 'Close', {
          duration: 4000, panelClass: ['warning-snackbar']
        });
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.cdr.markForCheck();
        return;
      }
    }

    if (this.selectedBeatRow[0].SUB_TOTAL >= 0 && totalSubAmount > this.selectedBeatRow[0].SUB_TOTAL) {
      this._snackBar.open('Please enter the BEAT benefit amount less than Sub Total.', 'Close', {
        duration: 4000, panelClass: ['warning-snackbar']
      });
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.cdr.markForCheck();
      return;
    } else if (this.selectedBeatRow[0].SUB_TOTAL < 0 && totalSubAmount < this.selectedBeatRow[0].SUB_TOTAL) {
      this._snackBar.open('Please enter the BEAT benefit less than Sub Total.', 'Close', {
        duration: 4000, panelClass: ['warning-snackbar']
      });
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.cdr.markForCheck();
      return;
    }

    this.saveBeatTypeForm().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000, panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Beat by Consol Amount has been successfully saved/updated', 'Close', {
            duration: 4000, panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000, panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  private saveBeatTypeForm() {
    const modalData = [];
    for (const row of this.beatTypeList) {
      const returnObj: any = {
        ADJ_TYPE_KEY: row.selectedBeatTypeCode,
        ADJ_PERCENT: row.beatWhPercent,
        AMOUNT: row.amount,
        BEAT_PAYMENT: row.beat_payment,
        GIE_KEY: this.selectedBeatRow[0].GIE_KEY,
        COMBINATION_KEY: this.selectedBeatRow[0].COMBINATION_KEY,
        OPERATION_TYPE: row.OPERATION_TYPE,
        IS_SINGLE: 'S',
        GROUP_KEY: row.OPERATION_TYPE === 'I' ? null : row.groupKey,
        ADJUSTMENT_KEY: row.adjKey,
        ADJUSTMENT_NAME: null,
        COMMENTS: row.comments,
        FDII_KEY: row.issueID === undefined || row.issueID === 0 ? 0 : row.issueID,
        TRANS_TYPE: 'BEAT_ADJ',
      };

      const exists = this.foreignTradingPartnerCodeData.find(item => item.LE_NAME === row.selectedTradingPartner);
      if (exists) {
        returnObj.ADJUSTMENT_NAME = exists.COMBINATION_KEY;
      }

      modalData.push(returnObj);
    }

    return this.beatTypeConsolAddService.saveBeatTypeConsolAddForm(
      this.baseURLs.api,
      modalData,
      this.globalService,
      this.filterParams,
      this.rowData,
      this.USER_SETTINGS,
      this.GENERAL_CONFIG
    );
  }

  reset(): void {
    this.total = 0;
    this.totalBeatPayment = 0;

    if (!this.isEditMode) {
      this.beatTypeList = [this.createEmptyRow()];
    } else {
      this.beatTypeList = _.cloneDeep(this.copyBeatTypeList);
      this.syncAllRowDropdownItems();
      this.recalcTotals();
    }

    this.isSaveClicked = false;
    this.cdr.markForCheck();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
